import { useEffect, useState } from 'react'
import { Waveform, WaveformLoader, type TWaveformVariant } from '@ds'
import { sendEvent, type AnalyticsPlacementTypes } from '@core/analytics'
import { useHydratedValue } from '@muse-web/router'
import { useInView } from '@core/viewport'
import type { TAudioSimple, TPlaylist } from '@core/models'
import { convertMillisecondsToSeconds } from '@core/format'
import { useAudioPeaks } from '../hooks/useAudioPeaks'

import { setPlaybackContext } from '../helpers/playbackAnalyticsContext'
import { playerPlacementPayload } from '../helpers/playerEvents'
import { usePlayerTime } from '../hooks/usePlayerTime'
import { useAudioPlayback } from '../hooks/useAudioPlayback'
import { useAudioRef } from '../providers/AudioRefProvider'

type TWaveformContainerProps = {
    audio: TAudioSimple
    analyticsPlacement: AnalyticsPlacementTypes
    /** Playlist this track belongs to; defaults to a single-item playlist built from `audio`. */
    playlist?: TPlaylist
    variant?: TWaveformVariant
    className?: string
    loaderText?: string
    /** Gate peaks loading (e.g. off-screen). Peaks also require a non-empty `peaksUrl`. */
    enablePeaksLoad?: boolean
}

/**
 * Drives the shared DS `Waveform` from the player: fetches peaks, maps playback → progress, and turns
 * a seek into a player action + `play_button_click`. Renders the loader until peaks are available.
 */
export const WaveformContainer = ({
    audio,
    analyticsPlacement,
    playlist,
    variant = 'dark',
    className,
    loaderText,
    enablePeaksLoad = true,
}: TWaveformContainerProps) => {
    const { isActiveAudio, playFromSeconds } = useAudioPlayback(audio, playlist)
    const audioRef = useAudioRef()
    const currentTime = usePlayerTime(audio.id)
    const { ref, inView } = useInView<HTMLDivElement>()
    const [hasEnteredView, setHasEnteredView] = useState(false)

    useEffect(() => {
        if (inView) {
            setHasEnteredView(true)
        }
    }, [inView])

    const durationInSec = convertMillisecondsToSeconds(audio.duration)
    const progress = isActiveAudio && durationInSec > 0 ? (currentTime / durationInSec) * 100 : 0

    const { data: peaks } = useAudioPeaks({
        audioId: audio.id,
        sourceId: audio.source.id,
        peaksUrl: audio.peaksUrl,
        enabled: enablePeaksLoad && hasEnteredView && Boolean(audio.peaksUrl),
    })
    const peaksShown = useHydratedValue(peaks, undefined)

    const handleSeek = (seconds: number) => {
        sendEvent({
            name: 'play_button_click',
            payload: playerPlacementPayload(audio.id, analyticsPlacement),
        })
        setPlaybackContext('play', analyticsPlacement)
        playFromSeconds(seconds)
    }

    const handleScrub = (seconds: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = seconds
        }
    }

    if (!peaksShown || peaksShown.length === 0) {
        return (
            <WaveformLoader
                ref={ref}
                id={audio.id}
                variant={variant}
                text={loaderText}
                className={className}
                paused={!inView}
            />
        )
    }

    return (
        <Waveform
            peaks={peaksShown}
            progress={progress}
            durationInSec={durationInSec}
            onSeek={handleSeek}
            onScrub={handleScrub}
            variant={variant}
            className={className}
        />
    )
}
